<template>
  <div class="main-content-container reports-list-container hr-reports">
    <!-- Page header -->
    <div class="top-content-container" style="padding-bottom: 0">
      <div class="hr-eyebrow">Reports</div>
      <h1 class="hr-h1">Reports</h1>

      <!-- Tab switcher -->
      <div class="hr-tabs">
        <button
          v-for="tab in visibleTabs"
          :key="tab.id"
          :class="['hr-tab', { active: activeView === tab.id }]"
          @click="activeView = tab.id"
        >
          <b-icon :icon="tab.icon" font-scale="0.85" />
          {{ tab.label }}
          <span v-if="tab.badge" class="dot">{{ tab.badge }}</span>
        </button>
      </div>
    </div>

    <!-- ====== Customer summary (default tab) ====== -->
    <div v-if="activeView === 'summary'">
      <!-- Filters bar (redesigned) -->
      <div class="hr-bar">
        <div class="hr-field">
          <label>From</label>
          <input
            class="hr-input"
            type="date"
            v-model="fromDate"
            @change="onFromDateChange"
            data-cy="fromDate"
          />
        </div>
        <div class="hr-field">
          <label>To</label>
          <input
            class="hr-input"
            type="date"
            v-model="toDate"
            @change="onToDateChange"
            data-cy="toDate"
          />
        </div>
        <!-- EA filter for Admin (all EAs) and Owner (EAs on their customers) -->
        <div class="hr-field" v-if="role === 'admin' || isOwnerUser">
          <label>EA</label>
          <select
            class="hr-select"
            v-model="selectedPA"
            @change="onSelectedPAChange"
            data-cy="select-assistant"
          >
            <option value="">All EA</option>
            <option
              v-for="opt in assistantsList"
              :key="opt.value"
              :value="opt.value"
              >{{ opt.text }}</option
            >
          </select>
        </div>

        <!-- EA filter for EA users (All EA / My data) -->
        <div class="hr-field" v-else>
          <label>EA</label>
          <select
            class="hr-select"
            v-model="eaFilter"
            @change="onEAFilterChange"
            data-cy="select-ea-filter"
          >
            <option value="all">All EA</option>
            <option value="my">My data</option>
          </select>
        </div>
        <div class="hr-field">
          <label>Customer</label>
          <multiselect
            v-model="selectedCustomers"
            :options="customersList.filter(opt => opt.value)"
            multiple
            track-by="value"
            label="text"
            :close-on-select="false"
            :clear-on-select="false"
            placeholder="All customers"
            class="custom-multiselect customer-multiselect"
            data-cy="select-customer"
          >
            <template slot="selection" slot-scope="{ values, isOpen }">
              <span
                class="multiselect__single"
                v-if="values.length &amp;&amp; !isOpen"
                >{{ values.length }} customer{{
                  values.length !== 1 ? "s" : ""
                }}
                selected</span
              >
            </template>
          </multiselect>
        </div>
        <div class="hr-field">
          <label>Category</label>
          <select
            class="hr-select"
            v-model="selectedTaskType"
            @change="onSelectedTaskTypeChange"
          >
            <option
              v-for="opt in taskTypes"
              :key="opt.value"
              :value="opt.value"
              >{{ opt.text }}</option
            >
          </select>
        </div>
        <div class="hr-bar-right">
          <label class="hr-check">
            <input
              type="checkbox"
              v-model="csvNullvalues"
              @change="!csvNullvalues"
            />
            Include zero values
          </label>
          <button
            v-if="role === 'admin'"
            class="hr-btn"
            @click="exportMonthlyPdfUrl"
          >
            <b-icon icon="file-earmark-text" font-scale="0.9" /> PDF
          </button>
          <b-button
            v-if="role === 'admin'"
            class="hr-btn hr-btn-primary"
            :href="exportCsvUrl"
          >
            <b-icon icon="download" font-scale="0.9" /> Export CSV
          </b-button>
        </div>
      </div>

      <!-- KPI cards -->
      <div class="hr-kpis">
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="clock" font-scale="0.9" /> Total tracked
          </div>
          <div class="k-val">{{ summaryTotalHours }}</div>
          <div class="k-sub">{{ fromDate }} → {{ toDate }}</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="graph-up" font-scale="0.9" /> Billable
          </div>
          <div class="k-val" style="color: #0d9488">
            {{ summaryBillablePct }}
          </div>
          <div class="k-sub">{{ summaryBillableHrs }}h billable</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="building" font-scale="0.9" /> Active customers
          </div>
          <div class="k-val">{{ summaryActiveCustomers }}</div>
          <div class="k-sub">of {{ allCustomers.length }} total</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="people" font-scale="0.9" />
            {{ role === "admin" || isOwnerUser ? "EAs contributing" : "EA" }}
          </div>
          <div class="k-val">
            {{ role === "admin" || isOwnerUser ? summaryEACount : fullName }}
          </div>
          <div class="k-sub">across all tasks</div>
        </div>
      </div>

      <!-- Chart -->
      <div
        class="hr-card"
        style="margin-bottom: 18px; padding-bottom: 8px"
        v-if="chartData.length > 1"
      >
        <div
          style="display: flex; justify-content: space-between; align-items: center; padding: 16px 18px 0"
        >
          <div class="hr-section-title" style="padding: 0">
            Billable vs non-billable hours
          </div>
          <div class="hr-legend">
            <span><i style="background: #7fb2d4" />Billable</span>
            <span><i style="background: #1e3a5f" />Non-billable</span>
          </div>
        </div>
        <div style="height: 280px; padding: 12px 8px 0">
          <GChart
            ref="gChart"
            :events="chartEvents"
            type="ColumnChart"
            :data="chartData"
            :options="summaryChartOptions"
          />
        </div>
      </div>
      <div
        class="hr-card"
        v-if="isLoadingBarChart"
        style="padding: 40px; text-align: center"
      >
        <b-spinner variant="primary" />
        <p class="mt-3" style="color: #64748b">Loading chart…</p>
      </div>
      <div
        class="hr-card"
        v-if="!isLoadingBarChart && chartData.length === 1"
        style="padding: 40px; text-align: center"
      >
        <div class="hr-empty">
          <div class="e-ic"><b-icon icon="bar-chart" font-scale="1.2" /></div>
          <div style="font-weight: 700; color: #0f172a; margin-bottom: 4px">
            No chart data
          </div>
          <div style="font-size: 13px">Adjust date range or filters.</div>
        </div>
      </div>

      <!-- Customer table (redesigned) -->
      <div class="hr-card" style="margin-top: 18px">
        <table class="hr-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Subscription</th>
              <th v-if="role === 'admin' || isOwnerUser">EA</th>
              <th>Utilization</th>
              <th style="text-align: right">Billable rounded (hrs)</th>
              <th style="text-align: right">Billable unrounded (hrs)</th>
              <th style="text-align: right">Total Duration (hh:mm)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="allCustomers.length === 0">
              <td :colspan="role === 'admin' || isOwnerUser ? 7 : 6">
                <div class="hr-empty">
                  <div class="e-ic">
                    <b-icon icon="building" font-scale="1.2" />
                  </div>
                  <div
                    style="font-weight: 700; color: #0f172a; margin-bottom: 4px"
                  >
                    No activity in this range
                  </div>
                  <div style="font-size: 13px">
                    Widen the dates or switch on "Include zero values".
                  </div>
                </div>
              </td>
            </tr>
            <tr
              v-for="item in allCustomers"
              :key="item.id"
              class="hr-row-click"
              @click="navigate(item)"
              data-cy="report-list-cards"
            >
              <td>
                <div style="display: flex; gap: 11px; align-items: center">
                  <b-avatar
                    size="2.2rem"
                    :src="item.profile.avatarThumbnail"
                    v-if="item.profile.avatarThumbnail"
                  />
                  <span
                    v-else
                    class="hr-avatar"
                    :style="{ background: '#0891b2', border: 0 }"
                    >{{ item.customerInitials }}</span
                  >
                  <div>
                    <div style="font-weight: 600" data-cy="customer-name">
                      {{ item.customerFullName }}
                    </div>
                    <div
                      style="font-size: 12px; color: #94a3b8"
                      v-if="item.profile.company"
                    >
                      {{ item.profile.company }}
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <span class="hr-badge b-rose">{{
                  item.profile.membership || "N/A"
                }}</span>
              </td>
              <td v-if="role === 'admin' || isOwnerUser">
                <div
                  v-if="item.assistants && item.assistants[0]"
                  style="display: flex; gap: 12px"
                >
                  <span
                    class="ea-avatar"
                    :style="{
                      background: getAssistantColor(item.assistants[0].id)
                    }"
                  >
                    {{ item.assistants[0].assistantInitials }}
                  </span>
                </div>
                <span v-else style="color: #94a3b8; font-size: 12px">N/A</span>
              </td>
              <td>
                <div style="display: flex; align-items: center; gap: 9px">
                  <div class="hr-util">
                    <span
                      :style="{
                        width: getUtilizationPercentage(item) + '%',
                        background: getUtilizationColor(item)
                      }"
                    ></span>
                  </div>
                  <span class="num" style="font-size: 12px; color: #64748b">{{
                    getUtilizationDisplay(item)
                  }}</span>
                </div>
              </td>
              <td class="num" style="text-align: right">
                {{ item.roundedBillableHours }}
              </td>
              <td class="num" style="text-align: right; color: #64748b">
                {{ item.unroundedBillableHours }}
              </td>
              <td class="num" style="text-align: right; font-weight: 700">
                {{ item.durationInHoursAndMinutes }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Infinite loader -->
        <!-- v-if waits for role so the first fetch always has the EA filter
             available - mounting it early (before role is hydrated) and
             then forcing a second corrective fetch caused duplicate rows. -->
        <infinite-loading
          v-if="role"
          spinner="spiral"
          :identifier="reportID"
          :force-use-infinite-wrapper="false"
          @infinite="infiniteHandlerReportList"
        >
          <div
            slot="no-more"
            style="padding: 20px; text-align: center; color: #94a3b8; font-size: 13px"
          >
            No more reports to load.
          </div>
          <div
            slot="no-results"
            style="padding: 20px; text-align: center; color: #94a3b8; font-size: 13px"
          >
            No more reports to load.
          </div>
        </infinite-loading>
      </div>
    </div>

    <!-- ====== Subscriptions tab (admin only) ====== -->
    <SubscriptionsView
      v-if="activeView === 'subscriptions' && role === 'admin'"
      @notify="showToastNotification"
    />

    <!-- ====== Daily tab ====== -->
    <DailyView v-if="activeView === 'daily'" @notify="showToastNotification" />

    <!-- ====== Weekly overview tab ====== -->
    <WeeklyOverview
      v-if="activeView === 'weekly'"
      @notify="showToastNotification"
    />

    <!-- ====== Timer corrections tab ====== -->
    <TimerCorrections
      v-if="activeView === 'corrections'"
      @notify="showToastNotification"
    />

    <!-- ====== EA Timelog tab (admin only) ====== -->
    <EATimelog
      v-if="activeView === 'ea-timelog' && role === 'admin'"
      @notify="showToastNotification"
    />

    <!-- ====== Contract utilization tab (admin only) ====== -->
    <ContractUtilization
      v-if="activeView === 'contract-utilization' && role === 'admin'"
    />

    <!-- Toast notification -->
    <div class="hr-toast" v-if="toastMessage">
      <b-icon icon="check-circle" font-scale="1" /> {{ toastMessage }}
    </div>

    <!-- Legacy toast indicators (for PDF) -->
    <b-toast
      id="save-indicator"
      solid
      ref="saveIndicator"
      :no-close-button="true"
      :no-auto-hide="true"
      :no-fade="true"
      toaster="b-toaster-top-center"
      :visible="isSaveIndicatorVisible"
    >
      <div id="loading-icon-wrapper" v-show="indicatorsVisible.saving">
        <img src="@/assets/icons/save-loading.svg" />
      </div>
      <div id="saved-icon-wrapper" v-show="indicatorsVisible.saved">
        <img src="@/assets/icons/save-tick.png" />
      </div>
      <div id="save-indicator-text">
        {{ indicatorsVisible.saving ? "Sending..." : monthlyReportRespMsg }}
      </div>
    </b-toast>
    <b-toast
      id="save-error-indicator"
      solid
      :no-close-button="true"
      :no-auto-hide="true"
      :no-fade="true"
      toaster="b-toaster-top-center"
      v-model="indicatorsVisible.error"
    >
      <div id="error-icon-wrapper">
        <img src="@/assets/icons/save-error.svg" />
      </div>
      <div id="save-indicator-text-error">
        {{
          correctionError === "month"
            ? " Only current month duration can be edited"
            : " Error! Something went wrong"
        }}
      </div>
      <div id="save-indicator-close-icon">
        <img
          src="@/assets/icons/close_sidebar.svg"
          @click="closeErrorIndicator"
        />
      </div>
    </b-toast>
  </div>
</template>

<script>
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";
import {
  downloadReports,
  getCustomerReportGraphs,
  getFilterCustomers,
  getCustomerReports,
  sendPDFReport
} from "@/apis/customers";
import axios from "@/config/axios";
import { getCategories } from "@/apis/common";
import { getAssistantsList } from "@/apis/assistants";
import { getProfile } from "@/apis/auth";
import { ellipsify } from "@/utils/utils";
import { DEBOUNCE_DELAY, USER_ROLES } from "@/utils/constants";
import _ from "lodash";
import { BASE_URL, internalCustomerEmailSet } from "@/utils/constants";
import { mapState } from "vuex";
import { getCompaniesMinimal } from "@/apis/companies";
import Multiselect from "vue-multiselect";
import SubscriptionsView from "@/views/common/reports/SubscriptionsView.vue";
import DailyView from "@/views/common/reports/DailyView.vue";
import WeeklyOverview from "@/views/common/reports/WeeklyOverview.vue";
import TimerCorrections from "@/views/common/reports/TimerCorrections.vue";
import EATimelog from "@/views/common/reports/EATimelog.vue";
import ContractUtilization from "@/views/common/reports/ContractUtilization.vue";
momentDurationFormatSetup(moment);
export default {
  name: "ReportsList",
  components: {
    Multiselect,
    SubscriptionsView,
    DailyView,
    WeeklyOverview,
    TimerCorrections,
    EATimelog,
    ContractUtilization
  },
  computed: {
    minToDate() {
      if (this.fromDate === "") return null;
      return this.fromDate;
    },
    maxFromDate() {
      if (this.toDate === "") return null;
      return this.toDate;
    },
    exportCsvUrl() {
      let { fromDate, toDate } = this;
      let token = localStorage.getItem("user-token");
      return axios.getUri({
        url: `${BASE_URL}/reports/customers/export-csv`,
        params: {
          completed_gte: fromDate,
          completed_lte: toDate,
          token: token,
          assigned_to: this.selectedPA,
          created_for: this.selectedCustomerIds,
          categories: this.selectedTaskType,
          include_null_timer: this.csvNullvalues
        }
      });
    },
    // Comma-separated list of selected customer ids for the `created_for`
    // param (same convention the backend already uses for `categories`).
    selectedCustomerIds() {
      return this.selectedCustomers.map(c => c.value).join(",") || null;
    },
    ...mapState("authModule", ["role", "profileUserId", "token", "fullName"]),
    tableFields() {
      const fields = [
        {
          key: "customerFullName",
          label: "Customer",
          thClass: "reports-th",
          tdClass: "reports-td align-middle",
          sortable: true
        },
        {
          key: "membership",
          label: "Subscription type",
          thClass: "reports-th",
          tdClass: "reports-td align-middle"
        },
        {
          key: "ea",
          label: "EA",
          thClass: "reports-th",
          tdClass: "reports-td align-middle"
        },
        {
          key: "roundedBillableHours",
          label: "Billable rounded (hrs)",
          thClass: "reports-th",
          tdClass: "reports-td align-middle",
          sortable: true
        },
        {
          key: "unroundedBillableHours",
          label: "Billable unrounded (hrs)",
          thClass: "reports-th",
          tdClass: "reports-td align-middle",
          sortable: true
        },
        {
          key: "durationInHoursAndMinutes",
          label: "Total Duration (hh:mm)",
          thClass: "reports-th",
          tdClass: "reports-td align-middle",
          sortable: true
        }
      ];
      if (this.role !== "admin" && !this.isOwnerUser) {
        return fields.filter(f => f.key !== "ea");
      }
      return fields;
    },
    isSaveIndicatorVisible() {
      let ret = this.indicatorsVisible.saving || this.indicatorsVisible.saved;
      return ret;
    },
    isOwnerUser() {
      return this.role === USER_ROLES.OWNER;
    },
    visibleTabs() {
      const tabs = [
        { id: "summary", label: "Customer summary", icon: "building" },
        {
          id: "subscriptions",
          label: "Subscriptions",
          icon: "credit-card",
          adminOnly: true
        },
        { id: "daily", label: "Daily", icon: "clock" },
        {
          id: "weekly",
          label: this.role === "admin" ? "Weekly overview" : "My week",
          icon: "bar-chart"
        },
        { id: "corrections", label: "Timer corrections", icon: "shield-check" },
        {
          id: "ea-timelog",
          label: "EA Timelog",
          icon: "table",
          adminOnly: true
        },
        {
          id: "contract-utilization",
          label: "Contract Utilization",
          icon: "speedometer2",
          adminOnly: true
        }
      ];
      return tabs.filter(t => !t.adminOnly || this.role === "admin");
    },
    summaryTotalHours() {
      if (!this.allCustomers || this.allCustomers.length === 0) return "0:00";
      let totalSec = 0;
      this.allCustomers.forEach(item => {
        totalSec += item.trackersLog
          ? item.trackersLog.totalRoundedDuration
          : 0;
      });
      const h = Math.floor(totalSec / 3600);
      const m = Math.floor((totalSec % 3600) / 60);
      return `${h}:${String(m).padStart(2, "0")}`;
    },
    summaryBillableHrs() {
      if (!this.allCustomers || this.allCustomers.length === 0) return "0.0";
      let totalBillSec = 0;
      this.allCustomers.forEach(item => {
        totalBillSec += item.trackersLog
          ? item.trackersLog.totalRoundedBillableDuration || 0
          : 0;
      });
      return (totalBillSec / 3600).toFixed(1);
    },
    summaryBillablePct() {
      if (!this.allCustomers || this.allCustomers.length === 0) return "—";
      let totalSec = 0;
      let billSec = 0;
      this.allCustomers.forEach(item => {
        totalSec += item.trackersLog
          ? item.trackersLog.totalRoundedDuration
          : 0;
        billSec += item.trackersLog
          ? item.trackersLog.totalRoundedBillableDuration || 0
          : 0;
      });
      if (totalSec === 0) return "—";
      return Math.round((billSec / totalSec) * 100) + "%";
    },
    summaryActiveCustomers() {
      if (!this.allCustomers) return 0;
      return this.allCustomers.filter(c => {
        const dur = c.trackersLog ? c.trackersLog.totalRoundedDuration : 0;
        return dur > 0;
      }).length;
    },
    summaryEACount() {
      if (!this.allCustomers) return 0;
      const eaSet = new Set();
      this.allCustomers.forEach(c => {
        if (c.assistants)
          c.assistants.forEach(a => eaSet.add(a.id || a.firstName));
      });
      return eaSet.size || 1;
    },
    summaryChartOptions() {
      return {
        ...this.chartOptions,
        backgroundColor: "transparent",
        height: 270,
        chartArea: { left: 50, right: 20, top: 10, bottom: 30 },
        colors: ["#7fb2d4", "#1e3a5f"],
        isStacked: true,
        legend: { position: "none" },
        bar: { groupWidth: "60%" },
        hAxis: {
          textStyle: { fontSize: 11, color: "#94a3b8" },
          gridlines: { count: 0 }
        },
        vAxis: {
          ...this.chartOptions.vAxis,
          textStyle: { fontSize: 11, color: "#94a3b8" },
          gridlines: { color: "#eef2f6" }
        }
      };
    }
  },
  data() {
    return {
      activeView: "summary",
      toastMessage: null,
      generating: false,
      isLoadingHours: true,
      billableHours: null,
      isLoadingBarChart: true,
      durationList: [],
      legendTitles: [],
      expandHover: false,
      activeExpandHoverId: "",
      inactiveExpandHoverId: "",
      showInactiveExpandHover: true,
      eaFilter: "all",
      selectedPA: null,
      totalHours: 0,
      selectedCustomers: [],
      // Raw ids restored from the URL before customersList (which supplies
      // the {value,text} option objects the multiselect needs) has loaded.
      pendingCustomerIds: [],
      selectedTaskType: null,
      csvNullvalues: false,
      assistantsList: [
        {
          text: "EA name",
          value: null
        }
      ],
      taskTypes: [{ text: "Task type", value: null }],
      fromDate: "",
      toDate: "",
      allCustomers: [],
      customersList: [{ text: "Customer name", value: null }],
      params: {},
      paramsDownload: {},
      chartData: [],
      chartEvents: {
        ready: () => {
          let chartLegends = [...this.$refs.gChart.data];
          for (let i = 1; i < chartLegends.length; i++) {
            this.legendTitles.push(chartLegends[i]);
          }

          this.taskDetails = this.legendTitles.map(
            ([date, duration, tooltip]) => ({
              date,
              duration,
              tooltip
            })
          );
          let durationArr = this.taskDetails.map(item => item.duration);
          durationArr.forEach(item => {
            this.durationList.push(this.getDurationInHoursAndMinutes(item));
          });
          this.$nextTick(() => {
            this.isLoadingBarChart = false;
          }, 1000);
        }
      },
      chartOptions: {
        chart: {
          title: "Customer Report",
          subtitle: "Customer Report List"
        },
        tooltip: {
          isHtml: true
        },
        backgroundColor: "#F6F9FC",
        height: 400,
        colors: ["#9FCAE2", "#074F89"],
        legend: {
          position: "top",
          alignment: "end",
          textStyle: {
            fontName: "Graphik Regular",
            color: "#aaa",
            fontSize: 12
          }
        },
        hAxis: {
          textStyle: {
            color: "#aaa"
          }
        },
        vAxis: {
          ticks: [],
          textStyle: {
            color: "#aaa"
          }
        }
      },
      selectedCompany: [
        {
          text: "All Companies",
          value: null
        }
      ],
      companies: [],
      indicatorsVisible: {
        saving: false,
        saved: false,
        error: false
      },
      correctionError: "",
      monthlyReportRespMsg: "Saved",
      reportID: +new Date(),
      offsetReportList: 0
    };
  },
  watch: {
    // vue-multiselect's v-model doesn't reliably co-exist with an explicit
    // @input on the same component (both target Vue's merged "input"
    // listener array) - watch the bound array directly instead.
    selectedCustomers() {
      this.onSelectedCustomerChange();
    }
  },
  created() {
    // Restore filters from the URL (e.g. when navigating back from a
    // customer's report) instead of always resetting to this month/no
    // filters - mirrors the query keys ReportDetails already reads.
    const q = this.$route.query;
    this.fromDate = q.from || moment(1, "DD").format("YYYY-MM-DD"); // Get first day of current month
    this.toDate = q.to || moment.utc().format("YYYY-MM-DD");
    if (q.assistantId) this.selectedPA = q.assistantId;
    if (q.eaFilter) this.eaFilter = q.eaFilter;
    if (q.customerId) {
      this.pendingCustomerIds = q.customerId.split(",").filter(Boolean);
    }
    if (q.taskType) this.selectedTaskType = q.taskType;
  },
  mounted() {
    getProfile().then(() => {
      this.getTaskTypes();
      this.getCustomerNames();
      this.getReportsGraph();
      this.getAllPAs();
    });
    this.onFromDateChange = _.debounce(this.onFromDateChange, DEBOUNCE_DELAY);
    this.onToDateChange = _.debounce(this.onToDateChange, DEBOUNCE_DELAY);
    this.getCompanyList();
  },
  methods: {
    // Native <input type="date"> already guarantees YYYY-MM-DD; only the
    // range order needs checking here.
    datesValid() {
      return !this.fromDate || !this.toDate || this.fromDate <= this.toDate;
    },
    onToDateChange() {
      if (this.datesValid()) {
        this.onToDateInput();
      }
    },
    onFromDateChange() {
      if (this.datesValid()) {
        this.onFromDateInput();
      }
    },
    downloadCSV() {
      this.generating = true;
      this.paramsDownload = {
        completed_lte: this.toDate,
        completed_gte: this.fromDate,
        token: localStorage.getItem("user-token")
      };
      downloadReports(this.paramsDownload)
        .then(res => {
          let csv = res.data;
          var csvData =
            "data:application/csv;charset=utf-8," + encodeURIComponent(csv);
          let anchor = document.createElement("a");
          anchor.setAttribute("download", "Reports.csv");
          anchor.setAttribute("href", csvData);
          anchor.click();
          this.generating = false;
        })
        .catch(err => {
          console.log(err);
        });
    },
    onEAFilterChange() {
      if (this.eaFilter === "all") {
        this.selectedPA = null;
      }
      this.changeType();
      this.getReportsGraph();
    },
    onSelectedPAChange() {
      this.changeType();
      this.getReportsGraph();
    },
    onSelectedTaskTypeChange() {
      this.changeType();
      this.getReportsGraph();
    },
    onSelectedCustomerChange() {
      this.changeType();
      this.getReportsGraph();
    },
    onFromDateInput() {
      this.changeType();
      this.getReportsGraph();
    },
    onToDateInput() {
      this.changeType();
      this.getReportsGraph();
    },
    getAllPAs() {
      // eslint-disable-next-line no-debugger
      getAssistantsList().then(res => {
        this.assistantsList = res.data.results.map(item => {
          let name = `${item.user.firstName} ${item.user.lastName}`;
          return {
            text: ellipsify(name, 17),
            value: item.id
          };
        });
        this.assistantsList.unshift({
          value: null,
          text: "EA name"
        });
      });
    },
    populateCustomersTable(res) {
      this.totalHours = 0;
      this.allCustomers = [...res].map(item => {
        item.customerFullName = `${item.profile.firstName} ${item.profile.lastName}`;
        item.durationInHoursAndMinutes = this.getDurationInHoursAndMinutes(
          item.trackersLog.totalRoundedDuration
        );
        item.unroundedBillableHours = parseFloat(
          item.trackersLog.totalBillableHours || 0
        ).toFixed(2);
        item.roundedBillableHours = parseFloat(
          item.trackersLog.totalRoundedBillableHours || 0
        ).toFixed(2);
        item.unroundedNonBillableHours = parseFloat(
          item.trackersLog.totalNonBillableHours || 0
        ).toFixed(2);
        item.roundedNonBillableHours = parseFloat(
          item.trackersLog.totalRoundedNonBillableHours || 0
        ).toFixed(2);
        item.customerInitials = item.customerFullName.match(/\b(\w)/g).join("");
        item.assistants = item.assistants.map(i => {
          i.assistantFullName = `${i.firstName} ${i.lastName}`;
          i.assistantInitials = i.assistantFullName.match(/\b(\w)/g).join("");
          return i;
        });
        return item;
      });
      this.allCustomers.forEach(item => {
        this.totalHours += item.profile.membershipHours;
      });
      if (this.totalHours === 0) {
        this.totalHours = "N/A";
      }
    },
    getCustomerNames() {
      if (this.role === "admin" || this.isOwnerUser) {
        // Owners use the same shape as admin - the EA picker only narrows
        // within the customers the API already restricts them to.
        this.params = {
          completed_lte: this.toDate,
          completed_gte: this.fromDate,
          created_for: this.selectedCustomerIds,
          categories: this.selectedTaskType,
          assigned_to: this.selectedPA || null
        };
      } else if (this.role === "assistant") {
        // EA users: always show only customers they worked on
        this.params = {
          completed_lte: this.toDate,
          completed_gte: this.fromDate,
          created_for: this.selectedCustomerIds,
          categories: this.selectedTaskType,
          assigned_to: this.profileUserId
        };
      }
      getFilterCustomers(this.params)
        .then(res => {
          let customerOptions = res.data.results
            .filter(customer => customer.isActive)
            .map(customer => {
              return {
                value: customer.id,
                text: customer.fullname
              };
            });
          this.customersList = this.customersList.concat(customerOptions);
          // Restore multi-select from the URL now that options exist.
          if (this.pendingCustomerIds.length) {
            this.selectedCustomers = this.customersList.filter(opt =>
              this.pendingCustomerIds.includes(opt.value)
            );
            this.pendingCustomerIds = [];
          }
        })
        .catch(err => {
          console.log(err);
        });
    },
    getTaskTypes() {
      getCategories()
        .then(res => {
          this.taskTypes = res.data.results.map(item => {
            return {
              text: ellipsify(item.title),
              value: item.id
            };
          });
          this.taskTypes.unshift({
            text: "Task type",
            value: null
          });
        })
        .catch(err => {
          console.log(err);
        });
    },
    changeType() {
      this.allCustomers = [];
      this.offsetReportList = 0;
      this.reportID += 1;
      this.syncFiltersToUrl();
    },
    // Current filters as a URL query object (Google-search style). Shared by
    // syncFiltersToUrl (keeps this page's own URL current) and navigate
    // (hands the same snapshot to ReportDetails so its back arrow can push
    // straight back to it - relying on browser history alone breaks on
    // deep links/reloads, so we round-trip the filters explicitly instead).
    filterQuery() {
      const query = {};
      if (this.fromDate) query.from = this.fromDate;
      if (this.toDate) query.to = this.toDate;
      if (this.selectedPA) query.assistantId = this.selectedPA;
      if (this.eaFilter && this.eaFilter !== "all")
        query.eaFilter = this.eaFilter;
      if (this.selectedCustomers.length)
        query.customerId = this.selectedCustomerIds;
      if (this.selectedTaskType) query.taskType = this.selectedTaskType;
      return query;
    },
    syncFiltersToUrl() {
      this.$router.replace({ query: this.filterQuery() }).catch(() => {});
    },
    infiniteHandlerReportList($state) {
      const offset = this.offsetReportList;
      // A filter change bumps reportID and resets allCustomers via
      // changeType() - but a request in flight from *before* that change
      // can still resolve afterwards and append its (now stale) results on
      // top of the fresh ones. Snapshot reportID now and drop the response
      // if a newer request has since superseded this one.
      const requestID = this.reportID;
      // Default params
      let params = {
        completed_lte: this.toDate,
        completed_gte: this.fromDate,
        created_for: this.selectedCustomerIds,
        categories: this.selectedTaskType
      };
      if (this.role === "admin" || this.isOwnerUser) {
        params.assigned_to = this.selectedPA || null;
      } else if (this.role === "assistant") {
        // Which customers appear is always restricted server-side to ones
        // this EA has trackers on (regardless of assigned_to) - this only
        // controls whether the hours shown are just this EA's own
        // (assigned_to -> creator filter) or the customer's full combined
        // total across every EA, matching "My data" vs "All EA".
        params.assigned_to =
          this.eaFilter === "all" ? null : this.profileUserId;
      }
      getCustomerReports(30, offset, params)
        .then(res => {
          if (this.reportID !== requestID) return;
          let hours = 0;
          this.allCustomers = [...this.allCustomers, ...res.data.results];
          this.allCustomers.map(el => {
            if (!internalCustomerEmailSet.has(el.profile.email)) {
              hours += el.trackersLog.totalRoundedDuration;
            }
          });
          this.billableHours = this.getBillableHours(hours);
          this.populateCustomersTable(this.allCustomers);
          this.offsetReportList = res.data.offset;
          if (res.data.next === null) {
            $state.complete();
          } else {
            $state.loaded();
          }
        })
        .catch(err => {
          console.log(err);
        });
    },
    getReportsGraph() {
      this.isLoadingBarChart = true;
      this.chartData = [
        [
          "Date",
          "Billable-Actual Hours",
          { type: "string", role: "tooltip", p: { html: true } },
          "Non-billable-Actual Hours",
          { type: "string", role: "tooltip", p: { html: true } }
        ]
      ];
      if (this.role === "admin" || this.isOwnerUser) {
        this.params = {
          completed_lte: this.toDate,
          completed_gte: this.fromDate,
          created_for: this.selectedCustomerIds,
          categories: this.selectedTaskType,
          assigned_to: this.selectedPA || null
        };
      } else if (this.role === "assistant") {
        // See infiniteHandlerReportList - same "My data" vs "All EA" split.
        this.params = {
          completed_lte: this.toDate,
          completed_gte: this.fromDate,
          created_for: this.selectedCustomerIds,
          categories: this.selectedTaskType,
          assigned_to: this.eaFilter === "all" ? null : this.profileUserId
        };
      }
      getCustomerReportGraphs(this.params)
        .then(res => {
          let max =
            res.data.highestBillableTotal > res.data.highestNonbillableTotal
              ? res.data.highestBillableTotal
              : res.data.highestNonbillableTotal;
          res.data.trackers.map(item => {
            this.chartData.push([
              moment(item.date).format("DD/MM"),
              item.totalRoundedBillableTime / 3600,
              this.getCustomTooltip(
                moment(item.date).format("DD/MM/YY"),
                item.totalRoundedBillableTime
              ),
              item.totalRoundedNonbillableTime / 3600,
              this.getCustomTooltip(
                moment(item.date).format("DD/MM/YY"),
                item.totalRoundedNonbillableTime
              )
            ]);
          });
          max = Math.ceil(max / (3600 * 50)) * 50;
          max /= 5;
          this.chartOptions.vAxis.ticks = [];
          for (let i = 0; i <= 5; i++) {
            let seconds = max * i;
            let hours = (max * i).toFixed(0).toString();

            this.chartOptions.vAxis.ticks.push({
              v: seconds,
              f: hours
            });
          }
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.isLoadingBarChart = false;
        });
    },
    getCustomTooltip(title, seconds) {
      const hrsAndMins = (seconds / 3600).toFixed(2);
      return `<h6 class="primary--text font-sm  font-family-medium ml-3 py-3">${title}</h6>
          <h6 class="primary--text font-sm  font-family-medium ml-3"> ${hrsAndMins} Hours</h6>`;
    },
    getDurationInHoursAndMinutes(seconds) {
      return moment.duration(seconds, "seconds").format("hh:mm", {
        trim: false
      });
    },
    getDurationInHHMMSS(seconds) {
      return moment.duration(seconds, "seconds").format("hh:mm:ss", {
        trim: false
      });
    },
    getBillableHours(seconds) {
      return (seconds / 3600).toFixed(2);
    },

    navigate(item) {
      // carry over the full filter snapshot (not just assistantId/taskType)
      // so ReportDetails' back arrow can hand the exact same query back to
      // this page instead of losing the customer/EA-filter/date selection.
      let routeObj = {
        name: "ReportDetails",
        params: {
          profileId: item.id,
          from: this.fromDate,
          to: this.toDate
        },
        query: this.filterQuery()
      };
      this.$router.push(routeObj);
    },
    showCardHover(id) {
      this.activeExpandHoverId = id;
      this.expandHover = true;
      this.showInactiveExpandHover = true;
    },
    undoCardHover(id) {
      this.inactiveExpandHoverId = id;
      this.expandHover = false;
    },
    onContextFromDate(ctx) {
      // The following will be an empty string until a valid date is entered
      this.fromDate = ctx.selectedYMD;
    },
    onContextToDate(ctx) {
      // The following will be an empty string until a valid date is entered
      this.toDate = ctx.selectedYMD;
    },

    async getCompanyList() {
      const IS_ACTIVE = null;
      const GET_ALL = true;
      const {
        response: { data: { results: companies } = {} } = {},
        error
      } = await this.asyncWrap(getCompaniesMinimal, IS_ACTIVE, GET_ALL);

      if (error) {
        console.log(error);
        return;
      }

      const defaultValue = { value: null, text: "All Companies" };
      this.companies = [defaultValue];

      const dataValues = companies.map(({ id, name }) => ({
        value: id,
        text: name
      }));

      this.companies = [...this.companies, ...dataValues];
    },

    onSelectCompany(options) {
      const ALL_COMPANIES_VALUE = { text: "All Companies", value: null };

      // * we only want to reset the selection if "ALL COMPANIES" was just selected
      const lastOption = (options.slice(-1) || [])[0];
      const allIsPickedLast = lastOption?.value === null;

      this.selectedCompany = allIsPickedLast
        ? [ALL_COMPANIES_VALUE]
        : // * All Companies option can only be alone at anytime in the selection. If there are multiple selections
          // * we cannot have the All Companies option selected.
          removeAllCompaniesOption(options);

      function removeAllCompaniesOption(options) {
        return options.filter(
          ({ value }) => value !== ALL_COMPANIES_VALUE.value
        );
      }
    },

    exportMonthlyPdfUrl() {
      this.showSaveIndicator();
      let companyList = [];
      let isAllCompanies = false;
      companyList = this.selectedCompany?.map(data => {
        if (data.value) {
          return data.value;
        } else {
          isAllCompanies = true;
        }
      });
      isAllCompanies ? (companyList = []) : companyList;
      let data = {
        companies: companyList,
        start_date: this.fromDate,
        end_date: this.toDate
      };
      sendPDFReport(data)
        .then(res => {
          this.monthlyReportRespMsg = res.data.message;
          this.showSaveIndicatorSaved();
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.selectedCompany = [];
        });
    },
    showSaveIndicator() {
      this.indicatorsVisible.error = false;
      this.indicatorsVisible.saved = false;
      this.indicatorsVisible.saving = true;
    },
    showSaveIndicatorSaved() {
      this.indicatorsVisible.error = false;
      this.indicatorsVisible.saving = false;
      this.indicatorsVisible.saved = true;
      window.setTimeout(() => {
        this.indicatorsVisible.saved = false;
      }, 6500);
    },
    showSaveIndicatorError() {
      this.indicatorsVisible.saving = false;
      this.indicatorsVisible.saved = false;
      this.indicatorsVisible.error = true;
    },
    closeErrorIndicator() {
      this.indicatorsVisible.error = false;
      this.correctionError = "";
    },
    showToastNotification(msg) {
      this.toastMessage = msg;
      setTimeout(() => {
        this.toastMessage = null;
      }, 2200);
    },

    async asyncWrap(fn, ...args) {
      let response;
      let error;

      try {
        response = await fn(...args);
      } catch (caughtError) {
        error = caughtError;
      }

      return { response, error };
    },
    getUtilizationPercentage(item) {
      if (!item.trackersLog || !item.profile.membershipHours) return 0;
      const usedHours = item.trackersLog.totalRoundedDuration / 3600;
      const totalHours = item.profile.membershipHours;
      return Math.min(100, (usedHours / totalHours) * 100);
    },
    getUtilizationColor(item) {
      const pct = this.getUtilizationPercentage(item);
      if (pct > 90) return "#e11d48";
      if (pct > 70) return "#b45309";
      return "#0d9488";
    },
    getUtilizationDisplay(item) {
      if (!item.trackersLog || !item.profile.membershipHours) return "0.0/0h";
      const usedHours = item.trackersLog.totalRoundedDuration / 3600;
      const totalHours = item.profile.membershipHours;
      return `${usedHours.toFixed(1)}/${totalHours}h`;
    },
    getAssistantColor(assistantId) {
      const colors = [
        "#0891b2",
        "#db2777",
        "#334155",
        "#7c3aed",
        "#0d9488",
        "#b45309",
        "#2563eb",
        "#0e7490",
        "#c2410c",
        "#059669",
        "#7c2d12",
        "#4338ca"
      ];
      // Generate a consistent color based on assistant ID
      let hash = 0;
      for (let i = 0; i < assistantId.length; i++) {
        hash = assistantId.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    }
  }
};
</script>

<style scoped lang="scss">
.ea-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.action-btns {
  height: 2rem;
}
.customer-multiselect {
  min-width: 220px;
  font-size: 13px;

  ::v-deep .multiselect__tags {
    min-height: unset;
    padding: 7px 40px 0 11px;
    border: 1px solid #e2e8f0;
    border-radius: 9px;
  }
  ::v-deep .multiselect__select {
    height: 34px;
  }
  ::v-deep .multiselect__placeholder,
  ::v-deep .multiselect__single {
    margin-bottom: 6px;
    font-size: 13px;
  }
}
.reports-list-container {
  margin-bottom: 10rem;
}
.bar-graph-container {
  min-height: 40rem;
  border: 1px solid #dcebf4;
  overflow: hidden;
}
.bar-graph-skeleton-container:last-child {
  margin-right: 0 !important;
}
.card {
  cursor: pointer;
}
a {
  color: white;
}
.company-multiselect {
  max-width: 70%;
}
.multiselect .multiselect__tags {
  position: unset !important;
}
.monthly-pdf-container {
  .action-btns {
    height: 3rem;
  }
  .title {
    width: fit-content;
  }
}
.export-as-container {
  .action-btns {
    height: 3rem;
    width: fit-content;
  }
}

/* ---- Django-style scrollable reports table ---- */
.reports-table-scroll-wrapper {
  border: 1px solid #dcebf4;
  border-radius: 8px;
  max-height: 520px;
  overflow-y: auto;
  overflow-x: auto;
  position: relative;

  ::v-deep thead th {
    background-color: #f0f6fb !important;
    color: #074f89 !important;
    font-weight: 600;
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 2px solid #dcebf4;
    white-space: nowrap;
    vertical-align: middle;
    position: sticky !important;
    top: 0;
    z-index: 10 !important;
  }
}

.reports-django-table {
  margin-bottom: 0;

  ::v-deep tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #eaf4fb;
    }

    td {
      border-bottom: 1px solid #edf3f8;
      vertical-align: middle;
      padding: 0.75rem 1rem;
    }
  }

  ::v-deep tbody tr:last-child td {
    border-bottom: none;
  }
}
</style>

<style lang="scss">
$image-dimension: 25px;
$pink: #f35a63;
$dark-blue: #0a374b;

#save-indicator,
#save-error-indicator {
  border-radius: 999px;
  margin: auto;
  box-shadow: none;
  border: none;
  width: fit-content;
  div.toast-body {
    padding: 10px 5px;
    font-size: 16px;
    display: flex;
  }
}
#save-indicator:focus,
#save-error-indicator:focus {
  border: none;
  outline: none;
}

#save-indicator {
  // width: auto;
  div.toast-body {
    background: $dark-blue;
    border-radius: 999px;
    color: white;
    display: flex;
  }
  #loading-icon-wrapper {
    // flex: 1;
    > img {
      height: $image-dimension;
      width: $image-dimension;
      animation: spin 1s linear infinite;
    }
  }
  #saved-icon-wrapper {
    // flex: 1;
    > img {
      height: $image-dimension;
      width: $image-dimension;
      animation: growIn 1s;
      top: 50%;
      position: absolute;
      transform: translateY(-50%);
    }
  }
  #save-indicator-text {
    flex: 2;
    text-align: center;
  }
}
#save-error-indicator {
  width: 300px;
  display: flex;
  border-radius: 30px;
  background: #f35a63;
  display: flex;
  > div.toast-body {
    flex: 1;
  }
  #save-indicator-text-error {
    color: white;
    text-align: center;
  }
  #error-icon-wrapper {
    background: #f35a63;
    border-bottom-left-radius: 999px;
    border-top-left-radius: 999px;
    color: white;
    flex: 1;
    > img {
      height: $image-dimension;
      width: $image-dimension;
      margin-top: 45%;
    }
  }
  #save-indicator-text {
    color: white;
    background: #f35a63;
    flex: 8;
    text-align: center;
  }
  #save-indicator-close-icon {
    background: #f35a63;
    color: white;
    width: $image-dimension;
    height: $image-dimension;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
    > img {
      cursor: pointer;
      flex: 1;
      height: $image-dimension;
      width: $image-dimension;
      margin-top: 90%;
    }
    border-bottom-right-radius: 999px;
    border-top-right-radius: 999px;
  }
}
</style>
